import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { AlertCircle, Lightbulb, Send } from "lucide-react";

/**
 * FeedbackDialog — private "Send Feedback" modal accessible from the user menu.
 *
 * Users pick "Report a Problem" or "Share an Idea", type a short message,
 * and submit. All submissions land in the admin-only /admin/feedback inbox
 * (coaches + moderators cannot see them).
 */
const MAX_LEN = 4000;
const OPTIONS = [
  {
    key: "problem",
    label: "Report a Problem",
    hint: "Something broken, confusing, or wrong.",
    Icon: AlertCircle,
    color: "#EF4444",
  },
  {
    key: "idea",
    label: "Share an Idea",
    hint: "A feature or improvement you'd love.",
    Icon: Lightbulb,
    color: "#0D9488",
  },
];

export default function FeedbackDialog({ open, onOpenChange }) {
  const [ftype, setFtype] = useState("problem");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset each time the dialog re-opens so users don't see stale content.
    setFtype("problem");
    setMessage("");
    setSubmitting(false);
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const msg = message.trim();
    if (msg.length < 3) {
      toast.error("Add a few more details so we can help.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", { type: ftype, message: msg });
      toast.success("Thanks — feedback sent to the team 🙏");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6 sm:p-6"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900 dark:text-slate-100">Send feedback</DialogTitle>
          <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
            Only the admin team sees this. Coaches and other users cannot read your feedback.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4 mt-2" data-testid="feedback-form">
          {/* Type toggle: Problem vs Idea */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label="Feedback type">
            {OPTIONS.map(({ key, label, hint, Icon, color }) => {
              const active = ftype === key;
              return (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setFtype(key)}
                  data-testid={`feedback-type-${key}`}
                  className={`flex items-start gap-2.5 p-3 rounded-lg border-2 text-left transition-colors ${
                    active
                      ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20"
                      : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-teal-300"
                  }`}
                >
                  <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color }} />
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-snug mt-0.5">{hint}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Message */}
          <div className="space-y-1.5">
            <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 font-mono">
              Your message
            </label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
              rows={5}
              autoFocus
              placeholder={
                ftype === "problem"
                  ? "What went wrong? Steps to reproduce help us fix it faster."
                  : "What would make the app better for you?"
              }
              className="bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 rounded-lg resize-none"
              data-testid="feedback-message"
            />
            <div className="flex justify-between text-[10px] text-slate-400 dark:text-slate-500 font-mono">
              <span>Private · admin-only</span>
              <span>{message.length} / {MAX_LEN}</span>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-lg"
              data-testid="feedback-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || message.trim().length < 3}
              className="bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-lg font-semibold"
              data-testid="feedback-submit"
            >
              <Send className="w-4 h-4 mr-1.5" />
              {submitting ? "Sending…" : "Send feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
